import { Input } from '@domgen/base-design-system-2.0/atoms/Input';
import { Select } from '@domgen/base-design-system-2.0/atoms/Select';
import { AutoComplete } from '@domgen/shared/enums/autoComplete';
import { CircleCheck } from '../../../assets/icons/CircleCheck';
import { FormKey } from '../../../enums/formKey';
import { StepKey } from '../../../enums/stepKey';
import { FormChangeEvent } from '../../../hooks/useFormValidation';
import { ApplianceFormData } from '../../../models/form/appliance';
import { usProvinces } from '../constants/usProvinces';

type ManualAddressFormProps = {
  formValues: Partial<ApplianceFormData[StepKey.PERSONAL_DETAILS]>;
  errors: Record<string, string>;
  handleChange: (e: FormChangeEvent) => string | void;
  onBlur: (e: FormChangeEvent) => string | void;
  isMailingAddressPoBox?: boolean;
};

const poBoxAddressHint =
  "PO Box detected — this will be used for correspondence only. You'll add the product's install address on the next screen.";

export const ManualAddressForm = ({
  formValues,
  errors,
  handleChange,
  onBlur,
  isMailingAddressPoBox,
}: ManualAddressFormProps) => (
  <>
    <div className="col-span-8">
      <Input
        value={(formValues?.[FormKey.ADDRESS_LINE_1] as string) ?? ''}
        label="Address line 1"
        placeholder="Enter address line 1"
        name={FormKey.ADDRESS_LINE_1}
        onChange={handleChange}
        onBlur={onBlur}
        rightIcon={
          formValues?.[FormKey.ADDRESS_LINE_1] &&
          !errors[FormKey.ADDRESS_LINE_1] &&
          !isMailingAddressPoBox ? (
            <CircleCheck />
          ) : undefined
        }
        hasError={errors[FormKey.ADDRESS_LINE_1]}
        hasSuccess={
          isMailingAddressPoBox
            ? undefined
            : formValues?.[FormKey.ADDRESS_LINE_1] !== '' && !errors[FormKey.ADDRESS_LINE_1]
        }
        hintText={isMailingAddressPoBox ? poBoxAddressHint : undefined}
        autoComplete={AutoComplete.OFF}
      />
    </div>
    <div className="col-span-8">
      <Input
        value={(formValues?.[FormKey.SUB_BUILDING] as string) ?? ''}
        label="Address line 2 (Optional)"
        placeholder="Address line 2 (apartment, suite, etc.)"
        name={FormKey.SUB_BUILDING}
        onChange={handleChange}
        onBlur={onBlur}
        rightIcon={
          formValues?.[FormKey.SUB_BUILDING] && !errors[FormKey.SUB_BUILDING] ? (
            <CircleCheck />
          ) : undefined
        }
        hasSuccess={formValues?.[FormKey.SUB_BUILDING] !== '' && !errors[FormKey.SUB_BUILDING]}
        autoComplete={AutoComplete.OFF}
      />
    </div>
    <div className="col-span-8">
      <Select
        label="State"
        placeholder="Select a state"
        name={FormKey.STATE_CODE}
        hasError={errors[FormKey.STATE_CODE]}
        options={usProvinces}
        value={(formValues?.[FormKey.STATE_CODE] as string) ?? ''}
        hasSuccess={formValues?.[FormKey.STATE_CODE] !== '' && !errors[FormKey.STATE_CODE]}
        onChange={(value: string) =>
          handleChange({ target: { name: FormKey.STATE_CODE, value } } as FormChangeEvent)
        }
        onBlur={(value: string) =>
          onBlur({
            target: {
              name: FormKey.STATE_CODE,
              value,
            },
          } as FormChangeEvent)
        }
        maxHeight={320}
        autoComplete={AutoComplete.OFF}
      />
    </div>
    <div className="col-span-8 md:col-span-4">
      <Input
        value={(formValues?.[FormKey.CITY] as string) ?? ''}
        label="City"
        placeholder="Enter a city"
        name={FormKey.CITY}
        onChange={handleChange}
        onBlur={onBlur}
        rightIcon={
          formValues?.[FormKey.CITY] && !errors[FormKey.CITY] ? <CircleCheck /> : undefined
        }
        hasError={errors[FormKey.CITY]}
        hasSuccess={formValues?.[FormKey.CITY] !== '' && !errors[FormKey.CITY]}
        autoComplete={AutoComplete.OFF}
      />
    </div>
    <div className="col-span-8 md:col-span-4">
      <Input
        value={(formValues?.[FormKey.POSTAL_CODE] as string) ?? ''}
        label="Zip code"
        placeholder="Enter a zip code"
        name={FormKey.POSTAL_CODE}
        onChange={handleChange}
        onBlur={onBlur}
        rightIcon={
          formValues?.[FormKey.POSTAL_CODE] && !errors[FormKey.POSTAL_CODE] ? (
            <CircleCheck />
          ) : undefined
        }
        hasError={errors[FormKey.POSTAL_CODE]}
        hasSuccess={formValues?.[FormKey.POSTAL_CODE] !== '' && !errors[FormKey.POSTAL_CODE]}
        autoComplete={AutoComplete.OFF}
      />
    </div>
  </>
);
